import type { RouteLocationNormalized, Router } from 'vue-router';

// Recovery for the stale-bundle-after-deploy failure.
//
// Almost every route in this app is a lazy `() => import(...)`, so the view
// only arrives when the user navigates to it. A deploy rewrites every chunk
// filename (they are content-hashed) and deletes the old ones, so a tab that
// was already open when we shipped is holding a route table full of URLs that
// no longer exist. The next navigation fetches one, the server answers 404 —
// or, because the SPA fallback hands out index.html for anything it cannot
// find, answers 200 with HTML — and the dynamic import rejects. vue-router
// aborts the navigation and the app simply stops responding to clicks, with
// nothing on screen to say why and no way out but a manual refresh.
//
// Sentry has been recording exactly this: JAVASCRIPT-VUE-2V (Chrome, "Failed
// to fetch dynamically imported module: .../MyAccount-CtYeA_6g.js") and
// JAVASCRIPT-VUE-42 (Safari, "'text/html' is not a valid JavaScript MIME
// type." — the index.html fallback) are one bug wearing two browsers' wording.
//
// The recovery is a full document load at the route the user asked for: that
// re-fetches index.html, which references the chunk names the running deploy
// actually has. It must happen at most once per destination, or a genuinely
// missing chunk turns into a reload loop, which is worse than the broken
// navigation it is trying to fix.
//
// Not every stale chunk is a route's. `defineAsyncComponent` and the odd bare
// `import()` load chunks the router never hears about, and a route chunk's
// CSS can fail before the JS is even requested (JAVASCRIPT-VUE-47, "Unable to
// preload CSS for /assets/useCbtFormBase-….css"). Vite's preload helper
// announces every one of those on `window` as a `vite:preloadError` event
// before it rethrows, so a listener there is the catch-all. It reloads the
// document the user is on, but only once the router has had its chance: the
// router knows where the user was going, the window event does not.

/**
 * Wording differs per browser, and per whether the server 404s the dead chunk
 * or serves the SPA's index.html in its place. Matched case-insensitively
 * against the error message.
 */
const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module', // Chrome / Edge — chunk 404
  'error loading dynamically imported module', // Firefox
  'importing a module script failed', // Safari — chunk 404
  'is not a valid javascript mime type', // Safari — index.html served instead
  'failed to load module script', // Chrome — index.html served instead
  'unable to preload css', // Vite's preload helper, same cause
];

/** sessionStorage key holding the last reload we attempted. */
export const CHUNK_RELOAD_MARKER_KEY = 'gv:chunk-reload';

/**
 * How long a recorded attempt suppresses another one for the same
 * destination. Long enough to cover a full document load plus boot, short
 * enough that a second deploy later in the same session still recovers.
 */
export const CHUNK_RELOAD_WINDOW_MS = 20_000;

/**
 * The event Vite's preload helper dispatches on `window` (with the error as
 * `payload`) when a dynamic import or one of its preloaded dependencies fails.
 */
export const VITE_PRELOAD_ERROR_EVENT = 'vite:preloadError';

type ReloadMarkerStorage = Pick<Storage, 'getItem' | 'setItem'>;

// @allow-handwritten Injection seams for the browser globals this touches, not an API shape.
interface ChunkReloadOptions {
  /** Defaults to sessionStorage; `null` disables recovery (no loop guard). */
  storage?: ReloadMarkerStorage | null;
  /** Performs the full document load. Defaults to `location.assign`. */
  reload?: (href: string) => void;
  /** Clock, injectable so the guard window is testable. */
  now?: () => number;
}

// @allow-handwritten Injection seams for the browser globals this touches, not an API shape.
interface PreloadErrorRecoveryOptions extends ChunkReloadOptions {
  /** Where Vite dispatches the event. Defaults to `window`. */
  target?: Pick<EventTarget, 'addEventListener'>;
  /** URL of the document the failure happened in. Defaults to `location.href`. */
  currentHref?: () => string;
  /**
   * Runs `fn` once the failing import has finished rejecting, i.e. after the
   * router's error handler has run. Defaults to a macrotask, which is always
   * after the promise chain the rejection travels down.
   */
  defer?: (fn: () => void) => void;
}

type ReloadDeps = Required<Pick<ChunkReloadOptions, 'reload' | 'now'>> & { storage: ReloadMarkerStorage | null };

// Per-document state. A reload replaces the document, so this resets exactly
// when it should: the reloaded document has not fired a reload yet, and if
// its chunk is still missing that failure is reported rather than hidden.
let reloadFired = false;

// Chunk errors the router handler has seen, whether it reloaded for them or
// declined to. The preload fallback leaves those alone: it would otherwise
// reload the page the user was leaving, or retry a destination the router
// already gave up on.
const claimedByRouter = new WeakSet<object>();

/** Whether this document has already triggered a stale-chunk reload. */
export function hasChunkReloadFired(): boolean {
  return reloadFired;
}

/** Test seam: back to a freshly loaded document. */
export function resetChunkReloadState(): void {
  reloadFired = false;
}

export function isDynamicImportError(error: unknown): boolean {
  const message = error instanceof Error
    ? error.message
    : typeof error === 'string' ? error : '';

  if (!message) return false;

  const normalised = message.toLowerCase();
  return DYNAMIC_IMPORT_ERROR_PATTERNS.some(pattern => normalised.includes(pattern));
}

/**
 * Registers a `router.onError` handler that recovers from a lazy-route chunk
 * the running deploy no longer serves, by reloading the document at the route
 * the user was heading for. Any other navigation error is left alone.
 */
export function installChunkReloadRecovery(router: Router, options: ChunkReloadOptions = {}): void {
  const deps = reloadDeps(options, (href: string) => window.location.assign(href));

  router.onError((error, to) => {
    if (!isDynamicImportError(error)) return;

    // Claim it before deciding anything, so the preload fallback stays out
    // whether we reload or decline.
    if (typeof error === 'object' && error !== null) claimedByRouter.add(error);

    const href = destinationHref(router, to);
    if (!href) return;

    attemptReload(href, deps);
  });
}

/**
 * Listens for Vite's `vite:preloadError` and reloads the current document,
 * once, for a stale chunk the router did not recover from: a CSS preload, or
 * a lazy component or module imported outside the route table.
 */
export function installPreloadErrorRecovery(options: PreloadErrorRecoveryOptions = {}): void {
  // The page the user is on has committed, so a plain reload is the right
  // primitive here, unlike the router path.
  const deps = reloadDeps(options, () => window.location.reload());
  const target = options.target ?? window;
  const currentHref = options.currentHref ?? (() => window.location.href);
  const defer = options.defer ?? ((fn: () => void) => { setTimeout(fn, 0); });

  target.addEventListener(VITE_PRELOAD_ERROR_EVENT, (event) => {
    const error = (event as Event & { payload?: unknown }).payload;
    // Vite also announces a module whose top-level code threw; that is a
    // bug to report, not a chunk to re-fetch.
    if (!isDynamicImportError(error)) return;

    // Deliberately no preventDefault(): the helper must go on to throw, so
    // the router (which knows the destination) gets its turn, and so a
    // failure nobody recovers from still reaches the error reporter.
    defer(() => {
      if (typeof error === 'object' && error !== null && claimedByRouter.has(error)) return;
      attemptReload(currentHref(), deps);
    });
  });
}

function reloadDeps(options: ChunkReloadOptions, defaultReload: (href: string) => void): ReloadDeps {
  return {
    storage: options.storage === undefined ? readableSessionStorage() : options.storage,
    reload: options.reload ?? defaultReload,
    now: options.now ?? (() => Date.now()),
  };
}

function attemptReload(href: string, { storage, reload, now }: ReloadDeps): boolean {
  // Without storage there is no loop guard, and looping is worse than the
  // failure — leave the error to be reported instead.
  if (!storage) return false;

  const at = now();
  if (reloadAttemptedRecently(storage, href, at)) return false;
  if (!recordReloadAttempt(storage, href, at)) return false;

  reloadFired = true;
  reload(href);
  return true;
}

function readableSessionStorage(): ReloadMarkerStorage | null {
  try {
    return window.sessionStorage;
  } catch {
    // Safari with cookies blocked throws on the property access itself.
    return null;
  }
}

function destinationHref(router: Router, to: RouteLocationNormalized | undefined): string {
  const fullPath = to?.fullPath;
  if (!fullPath) return '';

  try {
    // Resolve so the history base is included; the raw fullPath is not a URL.
    return router.resolve(fullPath).href;
  } catch {
    return fullPath;
  }
}

function reloadAttemptedRecently(storage: ReloadMarkerStorage, href: string, at: number): boolean {
  let raw: string | null = null;
  try {
    raw = storage.getItem(CHUNK_RELOAD_MARKER_KEY);
  } catch {
    return false;
  }
  if (!raw) return false;

  let marker: { href?: unknown; at?: unknown };
  try {
    marker = JSON.parse(raw);
  } catch {
    return false;
  }

  if (marker?.href !== href || typeof marker.at !== 'number') return false;

  // A clock that moved backwards counts as recent: suppressing a reload is
  // always the safe side of this comparison.
  return at - marker.at < CHUNK_RELOAD_WINDOW_MS;
}

function recordReloadAttempt(storage: ReloadMarkerStorage, href: string, at: number): boolean {
  try {
    storage.setItem(CHUNK_RELOAD_MARKER_KEY, JSON.stringify({ href, at }));
    return true;
  } catch {
    // Quota or a storage-denying browser: no marker means no guard, so skip.
    return false;
  }
}
